import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Building2, Star, Stethoscope, Activity, Users, ShieldAlert } from 'lucide-react'
import { clinicaService } from '../../services/clinicaService'
import { veterinarioService } from '../../services/veterinarioService'
import { suscripcionClinicaService } from '../../services/suscripcionClinicaService'
import { usePermissions } from '../../hooks/usePermissions'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'

function extractArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

function extractEntity(payload) {
  if (!payload) return null
  if (payload?.data?.data && typeof payload.data.data === 'object') return payload.data.data
  if (payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) return payload.data
  if (typeof payload === 'object' && !Array.isArray(payload)) return payload
  return null
}

function getClinicaId(clinica) {
  return String(clinica?.id_clinica || clinica?.idClinica || clinica?.id || '')
}

function parseNumber(value, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeServices(clinica) {
  const fromArray =
    clinica?.servicios_ofrecidos ||
    clinica?.serviciosOfrecidos ||
    clinica?.servicios ||
    clinica?.especialidades ||
    []

  if (Array.isArray(fromArray)) {
    return fromArray
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        return String(item?.nombre || item?.name || '').trim()
      })
      .filter(Boolean)
  }

  if (typeof fromArray === 'string') {
    return fromArray
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return []
}

function getVetName(vet) {
  const nombre = vet?.usuario?.nombre || vet?.nombre || ''
  const apellido = vet?.usuario?.apellido || vet?.apellido || ''
  const fullName = `${nombre} ${apellido}`.trim()
  return fullName || 'Veterinario'
}

function getVetSpeciality(vet) {
  return (
    vet?.especialidad ||
    vet?.especialidades ||
    vet?.descripcion ||
    vet?.bio ||
    'Medicina general'
  )
}

function getVetRating(vet) {
  const rating = parseNumber(vet?.rating || vet?.calificacion || vet?.promedio_calificacion, 0)
  return rating > 0 ? rating : null
}

function getInitials(name) {
  const clean = String(name || '').trim()
  if (!clean) return 'V'
  const parts = clean.split(/\s+/).slice(0, 2)
  return parts.map((item) => item[0]?.toUpperCase() || '').join('') || 'V'
}

export default function ClinicaDetailMobileExact() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { isDueno } = usePermissions()

  const [clinica, setClinica] = useState(null)
  const [veterinarios, setVeterinarios] = useState([])
  const [suscripcion, setSuscripcion] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const [clinicaRes, veterinariosRes, suscripcionesRes] = await Promise.all([
        clinicaService.getById(id),
        veterinarioService.getAll({ id_clinica: id, per_page: 100 }).catch(() => null),
        suscripcionClinicaService.getAll().catch(() => null)
      ])

      const clinicaData = extractEntity(clinicaRes)
      const vetsData = extractArray(veterinariosRes)
      const suscripciones = extractArray(suscripcionesRes)
      const currentSubscription = suscripciones.find(
        (item) => getClinicaId(item?.clinica) === String(id)
      )

      setClinica(clinicaData)
      setVeterinarios(vetsData)
      setSuscripcion(currentSubscription || null)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo cargar la clínica')
      setClinica(null)
      setVeterinarios([])
      setSuscripcion(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const serviceItems = useMemo(() => normalizeServices(clinica), [clinica])
  const isSubscribed = !!suscripcion && ['ACTIVA', 'PENDIENTE_APROBACION'].includes(suscripcion.estado)
  const rating = parseNumber(clinica?.rating || clinica?.calificacion, 4.5)
  const pacientesCount = parseNumber(clinica?.pacientes_count || clinica?.pacientesCount || clinica?.cantidad_pacientes, 0)
  const urgenciasLabel =
    clinica?.urgencias_24h === true || clinica?.emergencias_24h === true || clinica?.emergencia_24h === true
      ? '24h'
      : 'No'

  const handleToggleSubscription = async () => {
    if (!isDueno || !clinica) return
    try {
      setBusy(true)
      setError('')
      if (suscripcion?.id_suscripcion) {
        await suscripcionClinicaService.unsubscribe(suscripcion.id_suscripcion)
      } else {
        await suscripcionClinicaService.subscribe(id)
      }
      await fetchData()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo actualizar la suscripción')
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <LoadingState message="Cargando detalle de clínica..." />

  if (!clinica) {
    return (
      <div className="px-4 py-6">
        <EmptyState
          icon={Building2}
          title="Clínica no encontrada"
          message="No pudimos obtener la información de esta clínica."
        />
      </div>
    )
  }

  return (
    <div className="min-h-full bg-white pb-20">
      <div className="bg-gradient-to-br from-[#7a9a52] to-[#5b87d8] px-4 pb-4 pt-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="h-8 w-8 rounded-full text-white/90 flex items-center justify-center"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="text-center mt-1">
          <div className="text-[42px] leading-none">🏥</div>
          <h1 className="text-[20px] font-bold text-white mt-1">{clinica?.nombre || 'Clínica'}</h1>
          <p className="text-[12px] text-white/85 mt-1">
            {clinica?.direccion || clinica?.ciudad || 'Dirección no disponible'}
          </p>
        </div>
      </div>

      <div className="px-4 -mt-3">
        {isDueno ? (
          <button
            type="button"
            onClick={handleToggleSubscription}
            disabled={busy}
            className={`w-full rounded-full py-2.5 text-[12px] font-semibold shadow-soft ${
              isSubscribed ? 'bg-[#ff8c42] text-white' : 'bg-[#7a9a52] text-white'
            } disabled:opacity-60`}
          >
            {busy
              ? 'Procesando...'
              : isSubscribed
                ? `✓ ${suscripcion?.estado === 'PENDIENTE_APROBACION' ? 'Suscripción pendiente' : 'Suscrito a esta clínica'}`
                : 'Suscribirme a esta clínica'}
          </button>
        ) : (
          <div className="w-full rounded-full py-2.5 text-[12px] font-semibold text-center bg-[#eef1f5] text-[#6c7a86]">
            Suscripción disponible para dueños
          </div>
        )}
      </div>

      {error ? (
        <div className="px-4 pt-3">
          <div className="rounded-xl bg-[#fdeaea] border border-[#f4caca] px-3 py-2 text-[12px] text-[#b23a3a]">
            {error}
          </div>
        </div>
      ) : null}

      <div className="px-4 pt-3">
        <div className="grid grid-cols-4 gap-2">
          <StatCard value={rating.toFixed(1)} label="Rating" className="text-[#f5c542]" />
          <StatCard value={String(veterinarios.length)} label="Veterinarios" />
          <StatCard value={urgenciasLabel} label="Urgencias" className="text-[#7a9a52]" />
          <StatCard value={String(pacientesCount)} label="Pacientes" />
        </div>
      </div>

      <div className="px-4 pt-4">
        <h2 className="text-[13px] font-bold text-[#111] mb-2">Servicios — servicios_ofrecidos</h2>
        <div className="grid grid-cols-2 gap-2">
          {(serviceItems.length > 0 ? serviceItems : ['Consultas', 'Vacunas', 'Laboratorio', 'Cirugías']).map((service) => (
            <div key={service} className="bg-[#f7f9fc] rounded-[10px] px-3 py-2.5 flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-[#7a9a52]" />
              <span className="text-[12px] font-semibold text-[#333]">{service}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 pt-4 pb-6">
        <h2 className="text-[13px] font-bold text-[#111] mb-2">Veterinarios</h2>
        <div className="space-y-1">
          {veterinarios.length === 0 ? (
            <div className="rounded-xl bg-[#f8f9fa] border border-[#ececec] px-3 py-3 text-[12px] text-[#777] flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" />
              <span>No hay veterinarios asociados para mostrar.</span>
            </div>
          ) : (
            veterinarios.map((vet, index) => {
              const name = getVetName(vet)
              const speciality = getVetSpeciality(vet)
              const vetRating = getVetRating(vet)
              return (
                <div key={vet?.id_veterinario || vet?.idVeterinario || vet?.id || `${name}-${index}`} className="flex items-start gap-3 py-2.5 border-b border-[#f5f5f5]">
                  <span className="h-9 w-9 rounded-full bg-gradient-to-br from-[#5b87d8] to-[#7a9a52] text-white text-[12px] font-bold inline-flex items-center justify-center shrink-0">
                    {getInitials(name)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-[#111] truncate">{name}</p>
                    <p className="text-[11px] text-[#888] truncate inline-flex items-center gap-1">
                      <Activity className="w-3 h-3" />
                      {speciality}
                    </p>
                    {vetRating ? (
                      <p className="text-[11px] text-[#c9883a] mt-1 inline-flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        {vetRating.toFixed(1)}
                      </p>
                    ) : null}
                  </div>
                  <Users className="w-4 h-4 text-[#c9cfd6] mt-1" />
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({ value, label, className = '' }) {
  return (
    <div className="bg-[#f7f9fc] rounded-xl px-2 py-2.5 text-center">
      <p className={`text-[16px] leading-none font-bold text-[#111] ${className}`}>{value}</p>
      <p className="text-[10px] text-[#888] mt-1">{label}</p>
    </div>
  )
}

